import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Cpu, Code, Palette, Database, Shield, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';

const ServiceCard = ({
  title,
  description,
  icon,
  link
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
}) => (
  <div className="bg-white rounded-xl shadow-luxury p-8 hover:shadow-luxury-xl transition-all duration-300 h-full">
    <div className="bg-gold/10 p-4 w-16 h-16 flex items-center justify-center rounded-xl mb-6">
      {icon}
    </div>
    <h3 className="text-2xl font-bold mb-4 text-luxury-900">{title}</h3>
    <p className="text-luxury-600 mb-6">{description}</p>
    <Link to="/booking" className="inline-block" onClick={() => window.scrollTo(0, 0)}>
      <Button variant="outline" className="text-gold border-gold hover:bg-gold/10 group w-full justify-center md:w-auto md:justify-start">
        Learn More
        <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Button>
    </Link>
  </div>
);

const Services = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-grow">
        {/* Hero Section with black background and wave */}
        <PageHeader
          title={<>What We <span className="text-gold">Do</span></>}
          subtitle="Our AI-powered development solutions deliver luxury-quality web applications at a fraction of traditional agency costs."
        />

        {/* Section 1: Development Services */}
        <section className="py-20 px-6 bg-gray-50">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Code className="w-4 h-4" />
                <span>Build Your Vision</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Development Services</h2>
              <p className="max-w-2xl mx-auto text-zinc-950">
                From MVPs to enterprise applications—we build beautiful, functional web apps that help businesses grow.
              </p>
            </div>

            {/* Services grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <ServiceCard
                title="Custom Web Applications"
                description="Beautiful, responsive web applications designed to provide exceptional user experiences and solve real business problems."
                icon={<Code className="h-8 w-8 text-gold" />}
                link="/booking"
              />

              <ServiceCard
                title="UI/UX Design"
                description="Intuitive, visually stunning interfaces that delight your users while achieving your business objectives."
                icon={<Palette className="h-8 w-8 text-gold" />}
                link="/booking"
              />

              <ServiceCard
                title="Internal Tools"
                description="Custom dashboards and operational tools that streamline your business processes and save you valuable time."
                icon={<Database className="h-8 w-8 text-gold" />}
                link="/booking"
              />

              <ServiceCard
                title="AI Integration"
                description="Leverage the power of artificial intelligence to automate processes, gain insights, and create smarter applications."
                icon={<Cpu className="h-8 w-8 text-gold" />}
                link="/booking"
              />

              <ServiceCard
                title="E-commerce Solutions"
                description="Scalable online stores that provide seamless shopping experiences and help you sell more products."
                icon={<Code className="h-8 w-8 text-gold" />}
                link="/booking"
              />

              <ServiceCard
                title="Maintenance & Support"
                description="Ongoing technical support and updates to keep your web applications secure, fast, and up-to-date."
                icon={<Code className="h-8 w-8 text-gold" />}
                link="/booking"
              />
            </div>

            {/* Feature image section */}
            <div className="mt-20 mb-16">
              <div className="flex flex-col lg:flex-row items-center gap-12">
                <div className="w-full lg:w-1/2">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">How We're Different</h2>
                  <p className="mb-6 text-slate-950">
                    Most agencies charge $20,000+ for custom web applications, putting them out of reach for many businesses.
                    Our AI-enhanced development process allows us to deliver the same quality for a fraction of the cost.
                  </p>
                  <ul className="space-y-4 mb-8">
                    <li className="flex items-start">
                      <span className="bg-gold/10 p-1 rounded-full text-gold mr-3 mt-1">
                        <ArrowRight className="h-4 w-4" />
                      </span>
                      <span className="text-gray-950">Custom MPV, web applications and internal tools less than $5k</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-gold/10 p-1 rounded-full text-gold mr-3 mt-1">
                        <ArrowRight className="h-4 w-4" />
                      </span>
                      <span className="text-gray-950">Faster delivery without sacrificing quality</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-gold/10 p-1 rounded-full text-gold mr-3 mt-1">
                        <ArrowRight className="h-4 w-4" />
                      </span>
                      <span className="text-gray-950">Transparent pricing with no surprise fees</span>
                    </li>
                  </ul>
                </div>
                <div className="w-full lg:w-1/2">
                  <div className="relative rounded-2xl overflow-hidden shadow-luxury">
                    <img
                      src="https://images.unsplash.com/photo-1461749280684-dccba630e2f6"
                      alt="Web development process"
                      className="w-full h-auto object-cover aspect-[4/3]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-luxury-900/30 to-transparent"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Security Services */}
        <section className="py-20 px-6 bg-gradient-to-br from-blue-50 via-white to-blue-50">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 text-blue-500 text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                <span>Secure Your Innovation</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Security Services</h2>
              <p className="max-w-2xl mx-auto text-zinc-950">
                Used AI tools to build fast? We audit, fix, and secure your code before it becomes a liability.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <Card className="p-8 bg-white/50 backdrop-blur border-blue-500/20 hover:border-blue-500/50 transition-all duration-300">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="md:w-1/3">
                    <div className="w-20 h-20 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-6">
                      <Shield className="w-10 h-10 text-blue-500" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4">AI Code Security Audits</h3>
                  </div>

                  <div className="md:w-2/3">
                    <ul className="space-y-4 mb-6">
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        <span className="text-zinc-700">Vibe-Check Assessments for AI-generated code</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        <span className="text-zinc-700">Vulnerability Remediation & fixes</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        <span className="text-zinc-700">Access control & authentication hardening</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        <span className="text-zinc-700">3-month security guarantee</span>
                      </li>
                    </ul>

                    <Link to="/services/ai-code-security" onClick={() => window.scrollTo(0, 0)}>
                      <Button className="bg-blue-500 hover:bg-blue-600 text-white">
                        Get Your Free Vibe Check
                        <Shield className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Section 3: WordPress Automation */}
        <section className="py-20 px-6 bg-gradient-to-br from-purple-50 via-white to-purple-50">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 text-purple-500 text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                <span>Automate Your WordPress</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">WordPress AI Plugins</h2>
              <p className="max-w-2xl mx-auto text-zinc-950">
                Transform your WordPress site into an intelligent automation machine with custom AI-powered plugins.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-12">
              <Card className="p-6 bg-white/50 backdrop-blur border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 text-center">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="font-semibold mb-2">Chatbots & Support</h3>
                <p className="text-sm text-muted-foreground">24/7 AI customer assistance</p>
              </Card>

              <Card className="p-6 bg-white/50 backdrop-blur border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 text-center">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                  <Code className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="font-semibold mb-2">SEO & Content</h3>
                <p className="text-sm text-muted-foreground">Alt text & optimization</p>
              </Card>

              <Card className="p-6 bg-white/50 backdrop-blur border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 text-center">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                  <Database className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="font-semibold mb-2">Analytics & Insights</h3>
                <p className="text-sm text-muted-foreground">Intelligent data analysis</p>
              </Card>

              <Card className="p-6 bg-white/50 backdrop-blur border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 text-center">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                  <Cpu className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="font-semibold mb-2">Custom Plugins</h3>
                <p className="text-sm text-muted-foreground">Tailored to your needs</p>
              </Card>
            </div>

            <div className="text-center">
              <Link to="/wordpress-plugins" onClick={() => window.scrollTo(0, 0)}>
                <Button size="lg" className="bg-purple-500 hover:bg-purple-600 text-white">
                  Browse AI Plugins
                  <Sparkles className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-6 bg-gray-50">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Build, Secure, or Automate?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Get started with any of our services today. Fixed pricing, fast delivery, exceptional quality.
            </p>
            <Link to="/booking" onClick={() => window.scrollTo(0, 0)}>
              <Button size="lg" className="bg-gold hover:bg-gold/90 text-white">
                Get Started Today
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Services;
